/**
 * Virtual try-on modal — wraps the FittingBox FitMix widget.
 *
 * Replaces the ModalVTO class that lived in modal-dialog.js on the previous
 * theme. Two behavioural changes over that version:
 *
 *   1. The FittingBox SDK and the widget are created on first open, not on every
 *      page load, so the third-party script is off the critical path.
 *   2. Triggers are bound by event delegation, so badges that arrive later —
 *      quick view, AJAX collection paging, section re-renders — work without a
 *      re-init.
 */
if (!customElements.get('vto-modal')) {
  class VTOModal extends HTMLElement {
    constructor() {
      super();

      this.apiKey = this.dataset.apiKey;
      this.sdkSrc = this.dataset.sdkSrc;
      this.dialog = this.querySelector('[role="dialog"]');
      this.viewer = this.querySelector('.vto-modal__viewer');
      this.fitmix = null;
      this.sdkPromise = null;
      this.openedBy = null;
      this.sku = '';

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      if (!this.moved) {
        this.moved = true;
        document.body.appendChild(this);
      }

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);

      // Reveal the triggers now that the modal can actually answer a click. The
      // flag lives on <html> rather than on each trigger so badges rendered
      // later — quick view, AJAX paging — are covered too.
      document.documentElement.classList.add('vto-ready');
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    onDocumentClick(event) {
      const trigger = event.target.closest('[data-vto-trigger]');
      if (trigger) {
        event.preventDefault();
        this.open(trigger);
        return;
      }

      if (event.target.closest('[data-vto-close]') && this.hasAttribute('open')) {
        this.close();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.hasAttribute('open')) {
        this.close();
      }
    }

    /**
     * Collection cards carry the SKU of their first available variant. The PDP
     * also ships a variant id -> SKU map, because the SKU has to follow whatever
     * variant is currently selected.
     */
    resolveSku(trigger) {
      const layer = trigger.closest('[data-vto-sku-map]');

      if (layer) {
        try {
          const skus = JSON.parse(layer.dataset.vtoSkuMap);
          const input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
          const variantId = input && input.value;

          if (variantId && skus[variantId]) return skus[variantId];
        } catch (error) {
          console.warn('VTO: could not read the variant SKU map', error);
        }
      }

      return trigger.dataset.sku || '';
    }

    loadSdk() {
      if (window.FitMix) return Promise.resolve();
      if (this.sdkPromise) return this.sdkPromise;

      this.sdkPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = this.sdkSrc;
        script.onload = resolve;
        script.onerror = () => reject(new Error(`VTO: could not load ${this.sdkSrc}`));
        document.head.appendChild(script);
      });

      return this.sdkPromise;
    }

    createWidget() {
      if (this.fitmix) return Promise.resolve(this.fitmix);

      return new Promise((resolve) => {
        this.fitmix = window.FitMix.createWidget(
          this.viewer.id,
          {
            apiKey: this.apiKey,
            frame: this.sku,
            onStopVto: () => this.close(),
            onIssue: (data) => console.warn('VTO:', data)
          },
          () => resolve(this.fitmix)
        );
      });
    }

    async open(trigger) {
      this.openedBy = trigger;
      this.sku = this.resolveSku(trigger);
      this.show();

      try {
        await this.loadSdk();
        await this.createWidget();
      } catch (error) {
        console.warn(error);
        this.removeAttribute('data-vto-loading');
        this.setAttribute('data-vto-error', '');
        return;
      }

      this.removeAttribute('data-vto-loading');
      this.removeAttribute('data-vto-error');
      if (this.sku) this.fitmix.setFrame(this.sku);
      this.fitmix.startVto('live');
    }

    show() {
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      this.setAttribute('data-vto-loading', '');
      if (this.dialog) this.dialog.focus();
    }

    close() {
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
      this.removeAttribute('data-vto-loading');

      // stopVto throws if the widget never finished starting.
      try {
        if (this.fitmix) this.fitmix.stopVto();
      } catch (error) {
        /* nothing to stop */
      }

      if (this.openedBy) {
        this.openedBy.focus();
        this.openedBy = null;
      }
    }
  }

  customElements.define('vto-modal', VTOModal);
}
